import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TypingIndicator from './TypingIndicator';
import './ChatBody.css';

const ChatBody = ({ messages, onActionClick, isRefreshing, isTyping, showActionButtons }) => {
  const chatBodyRef = useRef(null);

  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages]);

  const actionButtons = [
    { icon: '📚', label: 'Browse Courses' },
    { icon: '🎯', label: 'Custom Training' },
    { icon: '💼', label: 'Professional Services' },
    { icon: '❓', label: 'FAQs' },
    { icon: '👤', label: 'Talk to an Advisor' },
    { icon: 'ℹ️', label: 'About EnterOne' }
  ];

  const LinkRenderer = ({ node, ...props }) => {
    return (
      <a {...props} target="_blank" rel="noopener noreferrer">
        {props.children?.length ? props.children : props.href}
      </a>
    );
  };


  return (
    <div className="chat-body" ref={chatBodyRef}>
      {isRefreshing && (
        <div className="refresh-indicator">
          <div className="spinner"></div>
          <p>Refreshing chat...</p>
        </div>
      )}
      
      {messages.map((message, index) => (
        <React.Fragment key={message.id}>
          <div className={`message ${message.type}`}>
            {message.type === 'bot' && (
              <div className="message-avatar">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="18" fill="#1DB9AA"/>
                  <path d="M18 10C18 10 15 10 13 12C11 14 11 18 11 18V23C11 23 11 24.5 12.5 24.5H23.5C23.5 24.5 25 24.5 25 23V18C25 18 25 14 23 12C21 10 18 10 18 10Z" fill="white"/>
                  <rect x="15.5" y="19.5" width="5" height="1.5" fill="#1DB9AA"/>
                  <circle cx="15.5" cy="16.5" r="1" fill="#1DB9AA"/>
                  <circle cx="20.5" cy="16.5" r="1" fill="#1DB9AA"/>
                </svg>
              </div>
            )}
            <div className="message-content">
              {message.type === 'bot' ? (
                <div className="markdown-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: LinkRenderer }}>
                    {message.text}
                  </ReactMarkdown>
                </div>
              ) : (
                <p>{message.text}</p>
              )}
              {message.links && message.links.length > 0 && (
                <div className="message-links">
                  {message.links.map((link, idx) => (
                    <a 
                      key={idx}
                      href={link.url}
                      className="message-link"
                      target={link.external ? "_blank" : "_self"}
                      rel={link.external ? "noopener noreferrer" : ""}
                    >
                      {link.text} →
                    </a>
                  ))}
                </div>
              )}
            </div>
            {message.type === 'user' && (
              <div className="message-avatar user-avatar">
                <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                  <circle cx="18" cy="18" r="18" fill="#667EEA"/>
                  <path d="M18 8C15.2386 8 13 10.2386 13 13C13 15.7614 15.2386 18 18 18C20.7614 18 23 15.7614 23 13C23 10.2386 20.7614 8 18 8Z" fill="white"/>
                  <path d="M24 28C24 24.134 21.3137 21 18 21C14.6863 21 12 24.134 12 28H24Z" fill="white"/>
                </svg>
              </div>
            )}
          </div>
          
          {/* Show action buttons after the first (welcome) message for first-time users */}
          {index === 0 && showActionButtons && (
            <div className="action-buttons">
              {actionButtons.map((button, btnIndex) => (
                <button 
                  key={btnIndex}
                  className="action-btn"
                  onClick={() => onActionClick(button.label)}
                >
                  <span className="action-icon">{button.icon}</span>
                  <span className="action-label">{button.label}</span>
                </button>
              ))}
            </div>
          )}
        </React.Fragment>
      ))}

      {/* Show typing indicator when bot is processing */}
      {isTyping && <TypingIndicator />}
    </div>
  );
};

export default ChatBody;

